import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { FileUpload } from '../ui/FileUpload';
import { ApplicationService } from '../../services/applicationService';

import type { Job } from '../../data/jobs';

interface ApplicationFormProps {
  job: Job;
  onSubmit: (data: ApplicationData) => void;
}

export interface ApplicationData {
  fullName: string;
  email: string;
  phone: string;
  resume: File | null;
  coverLetter: string;
  linkedinProfile?: string;
  whyJoinCura: string;
  success?: boolean;
  error?: string;
}

export const ApplicationForm: React.FC<ApplicationFormProps> = ({ job, onSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ApplicationData>();

  const onFormSubmit = async (data: ApplicationData) => {
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!resumeFile) {
        onSubmit({
          ...data,
          resume: resumeFile,
          success: false,
          error: 'Please upload your resume'
        });
        setIsSubmitting(false);
        return;
      }

      // Upload resume if provided
      let resumeUrl = '';
      if (resumeFile) {
        resumeUrl = await ApplicationService.uploadResume(resumeFile, data.email) || '';
      }

      // Prepare application data for Supabase
      const applicationData = {
        job_id: job.id,
        job_title: job.title,
        full_name: data.fullName,
        email: data.email,
        phone: data.phone,
        resume_url: resumeUrl,
        cover_letter: data.coverLetter,
        linkedin_profile: data.linkedinProfile || '',
        why_join_cura: data.whyJoinCura
      };

      // Submit to Supabase
      const result = await ApplicationService.submitApplication(applicationData);

      if (result.success) {
        // Success - trigger parent component success state
        onSubmit({
          ...data,
          resume: resumeFile,
          success: true
        });

        reset();
        setResumeFile(null);
      } else {
        // Handle error
        onSubmit({
          ...data,
          resume: resumeFile,
          success: false,
          error: result.error || 'Failed to submit application'
        });
      }
    } catch (error) {
      console.error('Application submission error:', error);
      onSubmit({
        ...data,
        resume: resumeFile,
        success: false,
        error: 'Failed to submit application. Please try again.'
      });
    }

    setIsSubmitting(false);
  };

  return (
    <GlassCard className="p-8 max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Apply for {job.title}</h2>
        <p className="text-gray-600 mb-8">
          Join our team and help improve healthcare for seniors
        </p>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">
              Full Name *
            </label>
            <input
              {...register('fullName', {
                required: 'Full name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' }
              })}
              className="glass-input w-full"
              placeholder="Enter your full name"
            />
            {errors.fullName && (
              <p className="text-red-500 text-sm mt-1">{errors.fullName.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              className="glass-input w-full"
              placeholder="your.email@example.com"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              {...register('phone', {
                required: 'Phone number is required',
                pattern: {
                  value: /^[+]?[1-9][\d]{0,15}$/,
                  message: 'Invalid phone number'
                }
              })}
              className="glass-input w-full"
              placeholder="+1 (866) 520-4146"
            />
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
            )}
          </div>

          {/* Resume Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">
              Resume *
            </label>
            <FileUpload onFileSelect={setResumeFile} />
            {!resumeFile && (
              <p className="text-red-500 text-sm mt-1">Resume is required</p>
            )}
          </div>

          {/* LinkedIn Profile */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">
              LinkedIn Profile (Optional)
            </label>
            <input
              {...register('linkedinProfile', {
                pattern: {
                  value: /^https:\/\/www\.linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/,
                  message: 'Please enter a valid LinkedIn profile URL'
                }
              })}
              className="glass-input w-full"
              placeholder="https://www.linkedin.com/in/yourprofile"
            />
            {errors.linkedinProfile && (
              <p className="text-red-500 text-sm mt-1">{errors.linkedinProfile.message}</p>
            )}
          </div>

          {/* Cover Letter */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">
              Cover Letter *
            </label>
            <textarea
              {...register('coverLetter', {
                required: 'Cover letter is required',
                minLength: { value: 100, message: 'Cover letter must be at least 100 characters' }
              })}
              className="glass-textarea w-full h-32"
              placeholder="Tell us about your experience and why you're interested in this role..."
            />
            {errors.coverLetter && (
              <p className="text-red-500 text-sm mt-1">{errors.coverLetter.message}</p>
            )}
          </div>

          {/* Why Join Cura */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">
              Why do you want to join Cura? *
            </label>
            <textarea
              {...register('whyJoinCura', {
                required: 'Please tell us why you want to join Cura',
                minLength: { value: 50, message: 'Please provide at least 50 characters' }
              })}
              className="glass-textarea w-full h-24"
              placeholder="Share your passion for improving senior healthcare..."
            />
            {errors.whyJoinCura && (
              <p className="text-red-500 text-sm mt-1">{errors.whyJoinCura.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="pt-4"
          >
            <GlassButton
              type="submit"
              disabled={isSubmitting || !resumeFile}
              className="w-full py-4 text-lg font-semibold"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting Application...
                </span>
              ) : (
                'Submit Application'
              )}
            </GlassButton>
          </motion.div>
        </form>
      </motion.div>
    </GlassCard>
  );
}; 